import { FC } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TableTab from "@/components/tabs/table-tab";
import { DEF_PG_SIZE_REPORTS } from "@/constants/maximum-page-size";
import { FILTER_COMPANIES, FILTER_STATUS, FILTER_DEPARTMENTS,FILTER_EXPIRY_REASONS, FILTER_SITES, FILTER_TAGHOLDERS } from "@/constants/filter-types";
import { useLocale } from "@/contexts/localeContext";
import { TableContextProvider } from "@/contexts/table/TableContextProvider";
import { TranslationKeys } from "@/contexts/TranslationKeys";
import { Filter } from "@/models/Filter";
import { ListQuery } from "@/models/ListQuery";
import { Lookup } from "@/models/Lookup";
import LookupTypes from "@/models/LookupTypes";
import CompanySearch from "@/models/search/CompanySearch";
import DepartmentSearch from "@/models/search/DepartmentSearch";
import EnrolmentSearch from "@/models/search/EnrolmentSearch";
import OperationalComplianceReportSearch from "@/models/search/OperationalComplianceReportSearch";
import SiteSearch from "@/models/search/SiteSearch";
import ExpiryReasonSearch from "@/models/search/ExpiryReasonSearch";
import { auditRunReport } from "@/services/auditService";
import { getCompanyNamesWithPagination } from "@/services/companyService";
import { getDepartmentNamesWithPagination } from "@/services/departmentService";
import { getEnrolmentNamesWithPagination } from "@/services/enrolmentService";
import { getOperationalComplianceReport } from "@/services/reportService";
import { getSiteNamesWithPagination } from "@/services/siteService";
import { getExpiryReasonNamesWithPagination } from "@/services/expiryReasonService";
import { getUserFiltersByView } from "@/services/userPreferenceService";
import { getErrorMessage, showError } from "@/services/utilities/ServiceError";
import { REPORTS_OPERATIONAL_COMPLIANCE } from "@/constants/menu-types";
import { OperationalComplianceReportChildColumns, OperationalComplianceReportColumns } from "@/models/ColumnDefinitions/OperationalComplianceReportColumns";
import { getCategoryNameWithPagination } from "@/services/categoryService";
import CategorySearch from "@/models/search/CategorySearch";

interface OperationalComplianceReportProps {
    reportId: string;
}

const OperationalComplianceReport: FC<OperationalComplianceReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const userLoginId = useSelector((state: any) => state.userInfo.id);
    const currentMenuDetail = REPORTS_OPERATIONAL_COMPLIANCE;
    const lookup: Lookup[] = [
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName,
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor:DepartmentSearch.find(item => item.accessor === "name")?.searchOnName,
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find( item => item.accessor === "name")?.searchOnName,
        },
        {
            name: "masterId",
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: EnrolmentSearch.find(item => item.accessor === "name")?.searchOnName,
        },
        {
            name: "expiryReasonId",
            values: [],
            dataMethod: getExpiryReasonNamesWithPagination,
            searchAccessor: ExpiryReasonSearch.find(item => item.accessor === "name")?.searchOnName,
        },
        {
            name: LookupTypes.profileId,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "status",
            values: [
                { value: "COMPLIANT", label: translate.byKey(TranslationKeys.compliant) },
                { value: "WARNING", label: translate.byKey(TranslationKeys.warning) },
                { value: "EXPIRED", label: translate.byKey(TranslationKeys.expired) }
            ],
            isSingleSelect: true
        }
    ]

    const getOriginalStaticFilter = async (filterKey: string): Promise<Filter> => {
        switch (filterKey) {
            case "masterId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.displayName),
                    options: [],
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            case "companyId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.company),
                    options: [],
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            case "departmentId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.department),
                    options: [],
                    selected: [{ label: translate.byKey(TranslationKeys.all),  value: "*", ignore: true, }],
                };
            case "siteId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.site),
                    options: [],
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            case "expiryReasonId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.complianceCondition),
                    options: [],
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            case "status":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: "status",
                    label: translate.byKey(TranslationKeys.status),
                    options: [
                        { value: "COMPLIANT", label: translate.byKey(TranslationKeys.compliant) },
                        { value: "WARNING", label: translate.byKey(TranslationKeys.warning) },
                        { value: "EXPIRED", label: translate.byKey(TranslationKeys.expired) }
                    ],
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            default:
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: filterKey,
                    options: [],
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }]};
        }
    };

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getOperationalComplianceReport(translate ,listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    };

    const initialiseUserPreferences = async (setDetails: any) => {
        const staticFilterKeys: string[] = ["masterId", "companyId", "departmentId", "siteId", "expiryReasonId", "status"];
        let customFilter: Filter[] = [];

        try {
            const response = await getUserFiltersByView(userLoginId, currentMenuDetail, reportId);

            if (response.find(filter => filter.filterId === FILTER_TAGHOLDERS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("masterId"));
            }
            if (response.find(filter => filter.filterId === FILTER_COMPANIES) === undefined) {
                customFilter.push(await getOriginalStaticFilter("companyId"));
            }
            if (response.find(filter => filter.filterId === FILTER_DEPARTMENTS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("departmentId"));
            }
            if (response.find(filter => filter.filterId === FILTER_SITES) === undefined) { 
                customFilter.push(await getOriginalStaticFilter("siteId"));
            }
            if (response.find(filter => filter.filterId === FILTER_EXPIRY_REASONS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("expiryReasonId"));
            }
            if (response.find(filter => filter.filterId === FILTER_STATUS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("status"));
            }
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId,staticFilterKeys);
        }
    };

    return <TableContextProvider
        maxPageSize={DEF_PG_SIZE_REPORTS}
        dataMethod={getDataMethod}
        initialiser={setDetails => initialiseUserPreferences(setDetails)}
        tableDescriptor={OperationalComplianceReportColumns}
        childColumns={OperationalComplianceReportChildColumns}
        searchDescriptor={OperationalComplianceReportSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}
        getOriginalStaticFilter={getOriginalStaticFilter}
        loadDataOnDemand
        runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}>
        <TableTab
            title={""}
            reportTitle={translate.byKey(TranslationKeys.operationalComplianceReport)}
            selectRows={false}
            useGroupedRows
            highlightExpandedParent
            childTableHeading={translate.byKey(TranslationKeys.complianceDetails)}
            cyName="operationalComplianceReport"/>
    </TableContextProvider>
};

export default OperationalComplianceReport;